import type { ComponentFactory, ComponentRef } from '@angular/core';
import {
  ComponentFactoryResolver,
  EventEmitter,
  Injectable,
  Injector,
  NgModuleRef,
  ViewContainerRef,
  inject,
} from '@angular/core';

import type { Observable } from 'rxjs';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import type { WysiwygAction, WysiwygEmbeddableComponent } from './wysiwyg-adapter.types';
import { WysiwygEmbeddableComponents } from './wysiwyg-adapter.types';

@Injectable()
export class WysiwygAdapterService {
  private readonly injector = inject(Injector);
  private readonly cfr = inject(ComponentFactoryResolver);

  public components$ = new BehaviorSubject<
    ReadonlyMap<string, ComponentRef<WysiwygEmbeddableComponent<any, any>>>
  >(new Map());

  private output$ = new EventEmitter<WysiwygAction<any, any>>();

  private embeddedComponents: Map<string, ComponentRef<WysiwygEmbeddableComponent<any, any>>> =
    new Map();

  private componentFactories: Map<string, ComponentFactory<WysiwygEmbeddableComponent<any, any>>> =
    new Map();

  private contexts: Map<string, Set<string>> = new Map();

  constructor() {
    const components = this.injector.get(WysiwygEmbeddableComponents, []);
    components.forEach(component => {
      const factory = this.cfr.resolveComponentFactory(component);
      this.componentFactories.set(factory.selector, factory);
    });
  }

  public flush(contextId: string): void {
    if (this.contexts.has(contextId)) {
      this.contexts.get(contextId)!.forEach(hostId => {
        if (this.embeddedComponents.has(hostId)) {
          this.embeddedComponents.get(hostId)!.destroy();
          this.embeddedComponents.delete(hostId);
        }
      });
    }
  }

  public ngHostAdd(
    contextId: string,
    elementIds: string[],
    injector: Injector,
    data: Map<string, { [key: string]: any }>,
  ): void {
    const safeContextId = this.getSafeContextId(contextId);
    const context = this.getContextElements(safeContextId);
    const ngModuleRef = injector.get(NgModuleRef);
    const vcRef = injector.get(ViewContainerRef);

    elementIds.forEach((hostId: string) => {
      const host = document.getElementById(hostId)!;
      const selector = host.tagName.toLowerCase();

      if (this.componentFactories.has(selector)) {
        const componentFactory = this.componentFactories.get(selector)!;

        const component = vcRef.createComponent(
          componentFactory,
          undefined,
          injector,
          undefined,
          ngModuleRef,
        );

        while (host.children.length) {
          host.children[0].remove();
        }

        host.appendChild(component.location.nativeElement);
        const destroy$ = new Subject<void>();

        const componentData = data.get(hostId);
        component.instance.input$.next(componentData);
        component.instance.output$
          .pipe(takeUntil(destroy$.asObservable()))
          .subscribe(action => this.output$.emit(action.withTrackingProps(safeContextId, hostId)));

        component.onDestroy(() => {
          destroy$.next();
          destroy$.complete();
          this.embeddedComponents.delete(hostId);
          context.delete(hostId);
        });

        this.embeddedComponents.set(hostId, component);
        context.add(hostId);

        component.changeDetectorRef.detectChanges();
        this.components$.next(this.embeddedComponents);
      }
    });
  }

  public ngHostRemove(elementIds: string[], contextId?: string): void {
    elementIds.forEach((hostId: string) => {
      if (this.embeddedComponents.has(hostId)) {
        this.embeddedComponents.get(hostId)!.destroy();
        this.components$.next(this.embeddedComponents);
      }
    });
  }

  public ngHostTransfer(
    contextId: string,
    elementIds: string[],
    injector: Injector,
    data: Map<string, { [key: string]: any }>,
  ): void {
    this.ngHostRemove(elementIds);
    this.ngHostAdd(contextId, elementIds, injector, data);
  }

  public ngHostUpdate(elementId: string, data: { [key: string]: any }): void {
    const cmp = this.embeddedComponents.get(elementId);
    if (!cmp) return;

    const {
      instance: { input$, state },
    } = cmp;
    input$.next({
      ...state,
      ...data,
    });
  }

  public getContextOutput(contextId: string): Observable<WysiwygAction<any, any>> {
    return this.output$.pipe(
      filter(action => !!action.trackingProps && action.trackingProps.contextId === contextId),
    );
  }

  private getContextElements(id: string): Set<string> {
    if (!this.contexts.has(id)) {
      this.contexts.set(id, new Set());
    }
    return this.contexts.get(id)!;
  }

  private getSafeContextId(contextId: string): string {
    return contextId ? contextId : 'global';
  }
}
